import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import Paper from "@material-ui/core/Paper";
import { BASIC_SPECS_SECTIONS } from "../../redux_store/jobState_Schemas";
import { SHOW_DESIGNER_MESSAGE, CUSTOM_COMPONENT_LIST_IS_OPEN, updateDesigner } from "../../redux_store/appState";
import * as CONFIG from "../../redux_store/configState";
import { BaseURL, deepCopy, fetchTimeout, toNumber } from "../../components/Common";
import MessageSupport from "./MessageSupport";
import BSL_Dialog from "../../components/BSL_Dialog";
import BSL_TextInput from "../../components/BSL_TextInput";
import BracketDesigner from "./BracketDesigner";
import { GetShapeUpdateString, GetColourUpdateString, GetBOMUpdateString } from "../layout/PaperFunctionHelper";
import { DStateActive, uploadBackup } from "../../redux_store/appStateHelper";
import { addNewHistoryLine } from "../../helper/Helper";
import { pricing_data_template, fielders_cust_types, special_factors_default } from "../../redux_store/configState";
import {
    updateBOMData,
    updateBOMOrderData,
    updateBOMFreightPOA,
    updateJobHistory,
    updateBOMOrderDataToCustomerOrder,
    getPastOrderLists,
} from "../../redux_store/jobState";

export function normaliseFreightPOA(data) {
    if (!data || typeof data !== "object") return {};
    const normalisedData = {};

    Object.keys(data).forEach((key) => {
        if (typeof data[key] === "object" && data[key] !== null) {
            normalisedData[key] = {
                Price: typeof data[key].Price === "number" ? data[key].Price : Number(data[key].Price) || 0,
                Invoice: data[key].Invoice || "TBD",
            };
        } else {
            normalisedData[key] = {
                Price: typeof data[key] === "number" ? data[key] : Number(data[key]) || 0,
                Invoice: "TBD",
            };
        }
    });

    return normalisedData;
}

export default (props) => {
    const pricefile_default = useSelector((state) => state.app.pricefile);
    const colour_map = useSelector((state) => state.config?.colour_map);
    const colour_arrays = useSelector((state) => state.config?.colour_arrays);
    const dist_levels = useSelector((state) => state.app.dist_levels);
    const special_factors = useSelector((state) => state.app.special_factors);
    const UNLOCKED = useSelector((state) => state.app.designer.UNLOCKED);
    const user = useSelector((state) => state.app.user);
    const job = useSelector((state) => state.job);
    const app = useSelector((state) => state.app);
    const config = useSelector((state) => state.config);

    const [pricefile, setPricefile] = useState(pricefile_default);
    const [designBracketMode, setDesignBracketMode] = useState("");
    const [editorItemMode, setEditorItemMode] = useState(null);
    const [bracketData, setBracketData] = useState(null);
    const [emailContentFreightPOA, setEmailContentFreightPOA] = useState("");
    const [poaEmailSubject, setPoaEmailSubject] = useState("");
    const [bubbleSetup, setBubbleSetup] = useState(false);
    const [showDialogFreightPOA, setShowDialogFreightPOA] = useState(false);
    const [showEmailFreightPOA, setShowEmailFreightPOA] = useState(false);
    const [targetProductFreightPOA, setTargetProductFreightPOA] = useState("");
    const [poaItem, setPOAItem] = useState(false);
    const [newFreightPOA, setNewFreightPOA] = useState(null);
    const [apiCallCount, setAPICallCount] = useState(0);

    const frameRef = useRef(null);

    const dispatch = useDispatch();
    const { toggleBomOpen, fast } = props;

    const USE_EPRICE = true;
    const MAX_RETRIES = 5;
    const RETRY_DELAY = 1000;
    const carportMode = job?.DesignParameters?.CarportMode === 1;
    const quoteParamsFreightPOA = job?.QuoteParameters?.BOM?.FreightPOA || {};
    const hashNow = GetShapeUpdateString() + GetColourUpdateString() + GetBOMUpdateString();
    const hashAfterLastBOMUpdate = job?.QuoteParameters?.BOM?.bomHashCode;

    window.job = { ...job };

    useEffect(() => {
        // Clean-up functions after BOMFrame closes
        return () => {
            dispatch(updateDesigner(CUSTOM_COMPONENT_LIST_IS_OPEN, false));
        };
    }, []);

    useEffect(() => {
        dispatch(
            updateBOMData(job, true, () => {
                // Update BOM with data from API and trigger re-render
                setAPICallCount(apiCallCount + 1);
            })
        );
    }, [dispatch, hashNow, app?.pricefile_tier, job?.Customer?.DistLevel]);

    useEffect(() => {
        if (user.Role >= 98) {
            setValueDelegate("loadingSystemList", true, 0);
            setDisplayOptions(); // needs to be before pricefile to set POA
            // setPriceFileData();
            setPricefile(pricefile_default);
            setValueDelegate("product_database", pricefile_default, 0);
            setDisplayOptions();
            setBOMData();
            setValueDelegate("loadingSystemList", false, 0);
        }
    }, [pricefile_default]);

    useEffect(() => {
        if (apiCallCount > 0) {
            setValueDelegate("loadingSystemList", true, 0);
            setValueDelegate("onlyGetPricing", fast, 0);
            setDisplayOptions(); // needs to be before pricefile to set POA
            setPriceFileData();
            setMiscellaneousData();
            setQuoteData();
            setDisplayOptions();
            setBOMData();
        }
    }, [apiCallCount]);

    useEffect(() => {
        setDisplayOptions();
        setBOMData();
        setValueDelegate("loadingSystemList", false, 0);
        setValueDelegate("onlyGetPricing", fast, 0);
    }, [props?.job?.QuoteParameters?.BOM?.OrderList, props?.job?.QuoteParameters?.BOM?.PastOrderLists]);

    const setQuoteData = () => {
        const quoteData = props?.job?.QuoteParameters?.BOM?.QuoteData ?? {};
        const fixedPriceKitsCopy = deepCopy(props?.job?.Distributor?.FixedPriceKits);
        const designTemplateName = job?.AreaSpecifications[0]?.SHAPE?.DesignTemplateName;

        if (carportMode && quoteData?.AreaSpecifications?.[0]) {
            quoteData.AreaSpecifications[0]["CarportBoltedFootings"] =
                props?.job?.AreaSpecifications?.[0]?.Footings?.length ?? 0;
        }

        quoteData["PricingData"] = GetPricingFactors();
        quoteData["CURRENT_DEALER"] = quoteData["CURRENT_DEALER"] || {};
        quoteData["CURRENT_DEALER"]["FixedPriceKits"] = fixedPriceKitsCopy || {};
        quoteData["BOMFilter"] =
            config?.design_options?.[job.DesignSummary]?.design_templates?.[designTemplateName]?.bom_items?.filter;

        setValueDelegate("quote_data", quoteData, 0);
    };

    const setBOMData = () => {
        let systemList = props?.job?.QuoteParameters?.BOM?.SystemList;
        let orderList = props?.job?.QuoteParameters?.BOM?.OrderList;

        if (systemList?.items?.length > 0) {
            // DMap Fielders Codes to new D365 if state is active
            if (DStateActive({ config }) && app?.pricefile?.DMap) {
                // Ensure app.pricefile.DMap exists
                systemList.items.forEach((item) => {
                    const lyvesCode = item.Fielders_Code;
                    if (app.pricefile.DMap.hasOwnProperty(lyvesCode)) {
                        item.Fielders_Code = app.pricefile.DMap[lyvesCode];
                    }
                });
            }

            setValueDelegate("freightPOA", job?.QuoteParameters?.BOM?.FreightPOA || {}, 0);
            setValueDelegate("system_items", systemList, 0);
            setValueDelegate("loadingSystemList", false, 0);
            setValueDelegate("onlyGetPricing", fast, 0);
        }

        if (orderList?.items) {
            setValueDelegate("order_items", orderList, 0);
        }
        if (props?.job?.QuoteParameters?.BOM?.PastOrderLists) {
            setValueDelegate("past_order_lists", props?.job?.QuoteParameters?.BOM?.PastOrderLists, 0);
            setValueDelegate("loadingSystemList", false, 0);
            setValueDelegate("onlyGetPricing", fast, 0);
        }
    };

    const setPriceFileData = () => {
        let pricefile_to_load = pricefile;

        if (
            job?.Customer?.DistLevel &&
            job?.Customer?.DistLevel !== "Retail" &&
            job?.Customer?.DistLevel !== job?.Distributor.LYVESAccount
        ) {
            if (app?.pricefile_tier && app?.pricefile_tier?.LYVESAccount === job?.Customer?.DistLevel) {
                pricefile_to_load = app?.pricefile_tier;
            } else {
                toggleBomOpen(false);
                dispatch({
                    type: SHOW_DESIGNER_MESSAGE,
                    message:
                        "Error: Pricing Tier has not been loaded for this job.... it may still be loading or there may be an error.",
                });
            }
        } else if (user.Role >= 0 && job?.Distributor?.LYVESAccount !== app?.user?.BusinessDetails?.LYVESAccount) {
            if (app?.pricefile_tier && app?.pricefile_tier?.LYVESAccount === job?.Distributor?.LYVESAccount) {
                pricefile_to_load = app?.pricefile_tier;
            } else {
                toggleBomOpen(false);
                dispatch({
                    type: SHOW_DESIGNER_MESSAGE,
                    message:
                        "Error: Pricing Account has not been loaded for this job.... it may still be loading or there may be an error.",
                });
            }
        }

        setValueDelegate("product_database", pricefile_to_load, 0);
    };

    const setDisplayOptions = () => {
        // Get the number of order items directly from the items array
        const num_order_items = props?.job?.QuoteParameters?.BOM?.OrderList?.items?.length || 0;
        const isStaff = user?.Role >= 90;

        let display_options = {
            locked:
                (!isStaff && job.OrderStatus === "PENDING" && !UNLOCKED) ||
                (!isStaff && job.OrderStatus === "ACCEPTED" && !UNLOCKED) ||
                job?.DOrderChanges != null,
            d_state_active: DStateActive({ config }),
            show_prices: false,
            show_eprices: false,
            use_eprice: USE_EPRICE,
            role: user.Role,
            expanded_links: false,
            engValid: props?.engValid,
            customise_order_list: num_order_items > 0,
            confirmButton: props.confirmButton || false,
            debug: window.debug || (window.test && user.Role >= 99),
            brand: window.brand,
            retained_percentage: 5,
            use_bom_splicing: job?.DesignParameters?.USE_BOM_SPLICING,
            poa: props?.job?.App?.temp_POA || props?.job?.POA,
            cut_length_extra: props?.job?.SiteParameters?.CONST_BEAM_CUT_EXTRA || (window.LYSAGHT ? 100 : 30),
            order_status: props?.job?.OrderStatus,
        };

        setValueDelegate("display_options", display_options, 0);
    };

    const setMiscellaneousData = () => {
        if (colour_map) setValueDelegate("colour_map", colour_map, 0);
        if (colour_arrays) setValueDelegate("colour_arrays", colour_arrays, 0);
        if (job?.DOrderChanges) setValueDelegate("d_item_changes", job.DOrderChanges, 0);
        if (hashAfterLastBOMUpdate === hashNow) {
            setValueDelegate("loadingSystemList", false, 0);
            setValueDelegate("onlyGetPricing", fast, 0);
        }
    };

    const setValueDelegate = (parameter, value, retries = MAX_RETRIES) => {
        let frame = window.frames["BOM"];

        if (!frame?.window?.SetVal) {
            if (retries <= 0) {
                return;
            }
            setTimeout(() => setValueDelegate(parameter, value, retries - 1), RETRY_DELAY);
            return;
        }

        if (typeof frame.window.SetVal !== "function") {
            return;
        }

        try {
            frame.window.SetVal(parameter, value);
        } catch (error) {
            if (retries > 0) {
                setTimeout(() => setValueDelegate(parameter, value, retries - 1), RETRY_DELAY);
            }
        }
    };

    const iFrameFunctions = () => {
        let frame = window.frames["BOM"];
        if (!frame) return;

        if (!frame.qq) frame.window.qq = {};

        frame.window.qq.thisClose = function (system_list_data) {
            if (!USE_EPRICE) system_list_data.total_eprice = system_list_data.total_price;
            system_list_data.pricing_timestamp = Date.now();
            if (!system_list_data) {
                toggleBomOpen(false, system_list_data);
            } else {
                dispatch(updateBOMOrderData(null, system_list_data)).then(() => toggleBomOpen(false, system_list_data));
            }
        };

        frame.window.qq.thisNotifyDesigner = function (component, status) {
            dispatch(updateDesigner(component, status));
        };

        frame.window.qq.thisRemoveOrderList = function () {
            dispatch(updateBOMOrderData({}));
        };

        frame.window.qq.thisSave = function (order_data, system_list_data) {
            if (!USE_EPRICE) system_list_data.total_eprice = system_list_data.total_price;
            if (!USE_EPRICE) order_data.total_eprice = order_data.total_price;

            system_list_data.pricing_timestamp = Date.now();
            order_data.pricing_timestamp = Date.now();

            let history = props?.job?.History;
            if (!history) history = [];

            let changes = [];
            let addChanges = true;

            if (order_data) {
                for (let i = 0; i < order_data.items?.length; i++) {
                    let item = order_data.items[i];

                    if (item.deleted || item.manually_added) {
                        // Check if the changes are already in the history
                        if (item.manually_added) {
                            changes.push({ ADD: item.Code });
                        }
                        if (item.deleted) {
                            changes.push({ REMOVE: item.Code });
                        }
                    }
                }

                for (let j = 0; j < history.length; j++) {
                    let existingChanges = history[j]["Change to BOM"];
                    if (existingChanges && arraysEqual(existingChanges, changes)) {
                        addChanges = false;
                        break;
                    }
                }

                // save to backup
                if (order_data?.items?.length && order_data?.items?.length > 2) {
                    uploadBackup(props?.job?.id, "Component_List_Backup_", order_data);
                }
            }

            if (addChanges) {
                let newHistory = addNewHistoryLine({
                    jobState: { history },
                    historyLine: { "Change to BOM": changes },
                });
                if (newHistory?.length > 0) {
                    history.push(newHistory[0]);
                }

                let job_id = props?.job?.id;
                let business_code = props?.job?.BusinessCode;
                dispatch(updateJobHistory({ business_code, job_id, new_history: history }));
            }

            // Function to check if two arrays are equal
            function arraysEqual(arr1, arr2) {
                if (arr1.length !== arr2.length) return false;

                // Sort arrays to ensure consistent order for comparison
                arr1 = arr1.slice().sort();
                arr2 = arr2.slice().sort();

                for (let i = 0; i < arr1.length; i++) {
                    const obj1 = { ...arr1[i] };
                    const obj2 = { ...arr2[i] };

                    // Exclude "Date" property from objects
                    delete obj1.Date;
                    delete obj2.Date;

                    if (JSON.stringify(obj1) !== JSON.stringify(obj2)) return false;
                }

                return true;
            }

            dispatch(updateBOMOrderData(order_data, system_list_data)).then(() => {
                if (system_list_data) toggleBomOpen(true, system_list_data);
            });
        };

        frame.window.qq.thisEmail = function (system_items) {
            let emailContent = [];

            let email_message = "";
            let subject = "";

            if (system_items?.items) {
                let has_insulated_panels = false;
                let ip_qty = 0;
                for (let item of system_items?.items) {
                    if (item?.Desc.includes("Insulated") && item?.Category === "STEEL ROOFING") {
                        has_insulated_panels = true;
                        ip_qty = item?.Qty.replaceAll("|", ", ");
                    }

                    if (
                        has_insulated_panels &&
                        item?.PromptCodes?.includes &&
                        item?.QtyCalculated &&
                        item.PromptCodes.includes("$$=BONDOR_DELIVERY") &&
                        item.QtyCalculated.TotalePriceLastCalculated <= 2
                    ) {
                        if (
                            props?.job?.SiteParameters?.Address?.FormattedAddress &&
                            !email_message.includes("Quote For Delivery")
                        ) {
                            email_message +=
                                "Please price Insulated Panel Quote For Delivery to " +
                                job?.SiteParameters?.Address?.FormattedAddress +
                                "\n\n";
                            subject += "Insulated Panel Delivery";
                        }
                        email_message += "Item: " + item["Code"] + " (" + item?.Desc + ")" + "\n";
                        email_message += "Qty & Length: " + ip_qty + "\n";
                    }
                }
                for (let item of system_items?.items) {
                    if (
                        (!quoteParamsFreightPOA[item["Code"]] &&
                            item?.PromptCodes &&
                            item?.QtyCalculated &&
                            item.PromptCodes.includes("$$=POA")) ||
                        item.QtyCalculated.TotalePriceLastCalculated > 200000 ||
                        !item.QtyCalculated.TotalePriceLastCalculated ||
                        Number.isNaN(item.QtyCalculated.TotalePriceLastCalculated)
                    ) {
                        if (!email_message.includes("missing prices")) {
                            if (email_message != "") email_message += "\n";
                            email_message +=
                                "Please price the following items, which are missing prices or are POA..." + "\n\n";
                            subject += (subject != "" ? " & " : "") + "Price Confirmation";
                        }
                        let qty = item?.Qty;
                        if (item.QtyCalculated) {
                            qty = item.QtyCalculated.Qty + item.QtyCalculated.LinkedQty;
                        }
                        email_message +=
                            "Item: " +
                            item.QtyCalculated.TotalQty() +
                            " x " +
                            item["Code"] +
                            "/" +
                            item["Fielders_Code"] +
                            " (" +
                            item?.Desc +
                            ")" +
                            (item?.Colour ? " in Colour:" + item?.Colour : "") +
                            "\n";
                        if (item.QtyCalculated.QtyString.includes("x")) {
                            email_message += "    - " + item.QtyCalculated.QtyString.replaceAll("|", ", ") + "\n";
                        }
                    }
                }
            }

            if (email_message != "") {
                subject += " Request for Job #" + job.id + " (" + job?.Customer?.JobName + ")";
            }
            setEmailContentFreightPOA(email_message);
            setPoaEmailSubject(subject);
            setShowEmailFreightPOA(true);
        };

        frame.window.qq.SetFreightPOA = function (product, details, poa_bom_item) {
            if (details === undefined) {
                setTargetProductFreightPOA(product);
                if (poa_bom_item) {
                    setPOAItem(true);
                } else {
                    setPOAItem(false);
                }
                setShowDialogFreightPOA(true);

                if (quoteParamsFreightPOA.hasOwnProperty(product)) {
                    const normalisedFreightPOA = normaliseFreightPOA(quoteParamsFreightPOA);
                    setNewFreightPOA(normalisedFreightPOA[product]);
                } else {
                    setNewFreightPOA({ Price: 0, Invoice: "TBD" });
                }
            } else {
                if (!(quoteParamsFreightPOA.hasOwnProperty(product) && quoteParamsFreightPOA[product] === details)) {
                    quoteParamsFreightPOA[product] = details;
                    dispatch(updateBOMFreightPOA(quoteParamsFreightPOA));
                    setValueDelegate("freightPOA", quoteParamsFreightPOA, 0);
                }
            }
        };

        frame.window.qq.DesignBracket = function (
            item,
            BracketData,
            page_setup,
            edit = false,
            editor_item_mode = false,
            editing_index = 0
        ) {
            setBracketData({
                BracketData: BracketData,
                item: item,
                page_setup: page_setup,
                editing_index: editing_index,
            });
            setDesignBracketMode(edit ? "LimitedEdit" : "View");
            setEditorItemMode(editor_item_mode);
        };

        frame.window.qq.ReloadOrderList = (filename) => {
            dispatch(updateBOMOrderDataToCustomerOrder(filename));
        };

        frame.window.qq.GetOrderListItems = () => {
            dispatch(getPastOrderLists());
        };

        frame.window.qq.BackupOrderList = (orderList) => {
            uploadBackup(props?.job?.id, "Component_List_Backup_", orderList);
        };
    };

    const GetPricingFactors = () => {
        let factors = pricing_data_template;

        let state = props?.job?.Distributor?.Address?.State;
        if (!state) state = user?.BusinessDetails?.Address?.State;
        if (!state) state = props?.job?.SiteParameters?.Address?.State;
        if (!state) state = "NSW";
        if (state === "ACT") state = "NSW";

        let lyves_account = job?.Distributor.LYVESAccount;
        if (
            job?.Customer?.DistLevel &&
            job?.Customer?.DistLevel !== "Retail" &&
            job?.Customer?.DistLevel !== job?.Distributor.LYVESAccount
        ) {
            if (app?.pricefile_tier && app?.pricefile_tier?.LYVESAccount === job?.Customer?.DistLevel) {
                lyves_account = app?.pricefile_tier?.LYVESAccount;
            }
        }

        factors.LYVESAccount = lyves_account;
        factors["overall_markup"] = get_special_factor("OVER ALL MARKUP", state, special_factors);

        for (let areaIndex in props?.job?.AreaSpecifications) {
            factors["area_pricing"][areaIndex] =
                GetFactorForDesignType(job, Number(areaIndex), state) * factors["overall_markup"];
        }

        let dist = "Trade";
        if (props?.job?.Distributor?.DistLevel) dist = props?.job?.Distributor?.DistLevel;
        if (props?.job?.Customer?.DistLevel) dist = props?.job?.Customer?.DistLevel;

        factors["fielders_material_markup_factor"] = get_cust_type(dist, dist_levels);
        factors["fielders_material_markup_factor_type"] = dist;
        factors["fielders_pricing_factors"]["FIELDERS"] = get_special_factor(
            "MATERIALS - FIELDERS",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["RHS"] = get_special_factor("MATERIALS - RHS", state, special_factors);
        factors["fielders_pricing_factors"]["SWAGE"] = get_special_factor("MATERIALS - SWAGE", state, special_factors);
        factors["fielders_pricing_factors"]["SLATS"] = get_special_factor("MATERIALS - SLAT", state, special_factors);
        factors["fielders_pricing_factors"]["POLYCARB"] = get_special_factor(
            "MATERIALS - POLYCARBONATE",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["CDECKPOLYCARB"] = get_special_factor(
            "MATERIALS - C-Dek 310 POLYCARBONATE",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["INFILLS"] = get_special_factor(
            "MATERIALS - INFILL",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["SOLARSPAN"] = get_special_factor(
            "MATERIALS - SOLARSPAN",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["HARDWARE"] = get_special_factor(
            "MATERIALS - HARDWARE",
            state,
            special_factors
        );
        factors["fielders_pricing_factors"]["BUYINS"] = get_special_factor(
            "MATERIALS - OTHER BUY-INS",
            state,
            special_factors
        );

        let dealer_discount = 0;
        if (props?.job?.Distributor?.discount) dealer_discount = props?.job?.Distributor?.discount;

        factors["fielders_material_discount_factor"] = dealer_discount ? dealer_discount / 100 : 0;
        factors["fielders_material_markup_overall"] =
            factors["fielders_material_markup_factor"] * (1 - factors["fielders_material_discount_factor"]);

        return factors;
    };

    const GetFactorForDesignType = (job, areaIndex, state) => {
        const design_type = job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].DesignType;
        let factor = 1;

        if (design_type === CONFIG.DESIGN_TYPES.FLAT) {
            factor = get_special_factor("FLAT", state, special_factors);
        } else if (design_type === CONFIG.DESIGN_TYPES.GALV_FLAT) {
            factor = get_special_factor("GALV_FLAT", state, special_factors);
        } else if (design_type === CONFIG.DESIGN_TYPES.PITCHED) {
            factor = get_special_factor("PITCHED", state, special_factors);
        } else if (design_type === CONFIG.DESIGN_TYPES.GABLE_PORTAL) {
            factor = get_special_factor("PITCHED", state);
            let end_state = null;

            if (
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].RoofType1 === "Hip" ||
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].RoofType2 === "Hip"
            ) {
                end_state = "CARPORT HIP";
            }

            if (
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].RoofType1 === "Dutch Gable" ||
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].RoofType2 === "Dutch Gable"
            ) {
                end_state = "CARPORT DUTCH GABLE";
            }

            if (
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].RoofType1 === "Gazebo" ||
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.SHAPE].RoofType2 === "Gazebo"
            ) {
                end_state = "CARPORT GAZEBO";
            }

            if (end_state) {
                factor = factor * get_special_factor(end_state, state, special_factors);
            }

            if (
                job.AreaSpecifications[areaIndex][BASIC_SPECS_SECTIONS.ROOF_SHEETING].RoofType.toUpperCase().includes(
                    "POLY"
                )
            ) {
                factor = factor * get_special_factor("CARPORT POLYCARBONATE", state, special_factors);
            }

            if (job.DesignParameters.Freestanding) {
                factor = factor * get_special_factor("CARPORT FREESTANDING", state, special_factors);
            }
        }

        return factor;
    };

    function bubbleIframeMouseMove(iframe) {
        // Save any previous onmousemove handler
        var existingOnMouseMove = iframe.contentWindow.onmousemove;

        // Attach a new onmousemove listener
        iframe.contentWindow.onmousemove = function (e) {
            // Fire any existing onmousemove listener
            if (existingOnMouseMove) existingOnMouseMove(e);

            // Create a new event for the this window
            var evt = document.createEvent("MouseEvents");

            // We'll need this to offset the mouse move appropriately
            var boundingClientRect = iframe.getBoundingClientRect();

            // Initialize the event, copying exiting event values
            // for the most part
            evt.initMouseEvent(
                "mousemove",
                true, // bubbles
                false, // not cancelable
                window,
                e.detail,
                e.screenX,
                e.screenY,
                e.clientX + boundingClientRect.left,
                e.clientY + boundingClientRect.top,
                e.ctrlKey,
                e.altKey,
                e.shiftKey,
                e.metaKey,
                e.buttons,
                null // no related element
            );

            // Dispatch the mousemove event on the iframe element
            iframe.dispatchEvent(evt);
        };

        // Save any previous onmousemove handler
        var existingOnMouseUp = iframe.contentWindow.onmouseup;

        // Attach a new onmousemove listener
        iframe.contentWindow.onmouseup = function (e) {
            // Fire any existing onmousemove listener
            if (existingOnMouseMove) existingOnMouseUp(e);

            // Create a new event for the this window
            var evt = document.createEvent("MouseEvents");

            // We'll need this to offset the mouse move appropriately
            var boundingClientRect = iframe.getBoundingClientRect();

            // Initialize the event, copying exiting event values
            // for the most part
            evt.initMouseEvent(
                "mouseup",
                true, // bubbles
                false, // not cancelable
                window,
                e.detail,
                e.screenX,
                e.screenY,
                e.clientX + boundingClientRect.left,
                e.clientY + boundingClientRect.top,
                e.ctrlKey,
                e.altKey,
                e.shiftKey,
                e.metaKey,
                e.buttons,
                null // no related element
            );

            // Dispatch the mousemove event on the iframe element
            iframe.dispatchEvent(evt);
        };
    }

    if (frameRef && frameRef.current && !bubbleSetup) {
        setBubbleSetup(true);
        bubbleIframeMouseMove(frameRef.current);
    }

    const UpdateBracketData = (item, new_bracket_data, bracketData) => {
        let editing_index = bracketData?.editing_index !== undefined ? bracketData?.editing_index : -1;

        let added = false;
        if (item.BracketData) {
            for (let key in item.BracketData) {
                if (item.BracketData[key] && (editing_index === -1 || toNumber(editing_index) === toNumber(key))) {
                    added = true;
                    item.BracketData[key]["SideTabLength"] = new_bracket_data["Length 2"];
                    item.BracketData[key]["ftt"] = new_bracket_data["ftt"];
                    if (new_bracket_data["ftt"] == 0 || new_bracket_data["ftt"] == 1)
                        item.BracketData[key]["TopTabLength"] = new_bracket_data["Length 1"];

                    if (new_bracket_data["ftt"] == 0) item.BracketData[key]["TopTabStyle"] = "Upright - Standard";
                    if (new_bracket_data["ftt"] == 1) item.BracketData[key]["TopTabStyle"] = "Upright - Long";
                    if (new_bracket_data["ftt"] == 2) item.BracketData[key]["TopTabStyle"] = "Folded";
                    if (new_bracket_data["ftt"] == 3) item.BracketData[key]["TopTabStyle"] = "None";

                    let topTab_default = -54;
                    if (bracketData?.TopTabStyle) {
                        if (bracketData.TopTabStyle === "Upright - Standard") {
                            topTab_default = 54;
                            if (bracketData.TopTabLength > 0) topTab_default = bracketData.TopTabLength;
                        } else if (bracketData.TopTabStyle === "Upright - Long") {
                            topTab_default = 108;
                            if (bracketData.TopTabLength > 0) topTab_default = bracketData.TopTabLength;
                        } else if (bracketData.TopTabStyle.includes("Folded")) topTab_default = -54;
                        else if (bracketData.TopTabStyle.includes("None")) topTab_default = 0;
                        else topTab_default = bracketData.TopTabLength;
                    } else {
                        topTab_default = bracketData.TopTabLength;
                    }
                }
            }

            if (!added && editing_index > -1) {
                let key = editing_index;
                item.BracketData[key] = bracketData.BracketData;
                item.BracketData[key]["SideTabLength"] = new_bracket_data["Length 2"];
                item.BracketData[key]["ftt"] = new_bracket_data["ftt"];
                if (new_bracket_data["ftt"] == 0 || new_bracket_data["ftt"] == 1)
                    item.BracketData[key]["TopTabLength"] = new_bracket_data["Length 1"];

                if (new_bracket_data["ftt"] == 0) item.BracketData[key]["TopTabStyle"] = "Upright - Standard";
                if (new_bracket_data["ftt"] == 1) item.BracketData[key]["TopTabStyle"] = "Upright - Long";
                if (new_bracket_data["ftt"] == 2) item.BracketData[key]["TopTabStyle"] = "Folded";
                if (new_bracket_data["ftt"] == 3) item.BracketData[key]["TopTabStyle"] = "None";
            }

            item["CL DATA"] = item.BracketData;
        }
        return item;
    };

    const get_cust_type = (name, dist_levels = null) => {
        if (!dist_levels) dist_levels = fielders_cust_types;
        for (const row of fielders_cust_types) {
            if (row.name === name) {
                return Number(row.factor);
            }
        }
        return 4.05;
    };

    const get_special_factor = (name, state, special_factors = null) => {
        let factorsArray = special_factors_default;
        if (special_factors)
            factorsArray = Array.isArray(special_factors) ? special_factors : Object.values(special_factors);

        for (const row of factorsArray) {
            if (row.name === name && row?.[state + "_factors"]) {
                return Number(row?.[state + "_factors"]);
            }
        }

        return 99999;
    };

    return (
        <Paper elevation={20} style={{ width: "100%", height: "100%" }}>
            {showEmailFreightPOA && emailContentFreightPOA && poaEmailSubject && (
                <MessageSupport
                    style={{ display: showEmailFreightPOA ? "flex" : "none" }}
                    show={showEmailFreightPOA}
                    setShowMessageSupport={setShowEmailFreightPOA}
                    messageType={"FREIGHT POA"}
                    defaultMessage={emailContentFreightPOA}
                    subject={poaEmailSubject}
                />
            )}

            {designBracketMode && (
                <BracketDesigner
                    bracketDataCollection={bracketData}
                    mode={designBracketMode}
                    onClose={() => {
                        setDesignBracketMode("");
                    }}
                    onUpdate={(item, new_bracket_data) => {
                        let frame = window.frames["BOM"];
                        if (!frame) return;
                        if (editorItemMode) {
                            let editor_item = frame.window.GetEditorItemState();
                            if (editor_item) {
                                editor_item = JSON.parse(editor_item);
                                if (editor_item && editor_item?.["CL ID"] == new_bracket_data?.["BL ID"]) {
                                    editor_item = UpdateBracketData(editor_item, new_bracket_data, bracketData);
                                    setValueDelegate("editor_item", editor_item);
                                }
                            }
                        } else {
                            let state = frame.window.GetBOMState();
                            let item_lists = JSON.parse(state);
                            if (item_lists?.order_items?.items) {
                                let order_items = item_lists?.order_items?.items;
                                for (let item of order_items) {
                                    if (item?.["CL ID"] == new_bracket_data?.["BL ID"]) {
                                        item = UpdateBracketData(item, new_bracket_data, bracketData);
                                    }
                                }
                                setValueDelegate("order_items", order_items);
                            }
                        }
                        setDesignBracketMode("");
                    }}
                />
            )}

            <iframe
                ref={frameRef}
                name="BOM"
                src={BaseURL("/bom/")}
                width="100%"
                height="100%"
                onLoad={iFrameFunctions}
            ></iframe>

            <div style={{ width: "4px", background: "#CCC", cursor: "e-resize" }}></div>

            {showDialogFreightPOA && targetProductFreightPOA != "" && (
                <BSL_Dialog
                    open={true}
                    onClose={() => {
                        setShowDialogFreightPOA(false);
                        setTargetProductFreightPOA("");
                    }}
                    button2Text="Close"
                    button2Click={() => {
                        setShowDialogFreightPOA(false);
                        setTargetProductFreightPOA("");
                    }}
                    button1Text="Save"
                    button1Click={() => {
                        dispatch(updateBOMData(job));
                        quoteParamsFreightPOA[targetProductFreightPOA] = newFreightPOA;
                        dispatch(updateBOMFreightPOA(quoteParamsFreightPOA));
                        setBOMData();
                        setShowDialogFreightPOA(false);
                        setTargetProductFreightPOA("");
                    }}
                >
                    <BSL_TextInput
                        label={`${targetProductFreightPOA} ${
                            poaItem ? "confirmed charge per EA/LM" : "Delivery Charge"
                        }`}
                        style={{ width: "240px", color: "white" }}
                        helperText={
                            poaItem
                                ? "(0 will unset the price)"
                                : "(0 will reset to the original cost for non-POA Suburbs/Postcodes)"
                        }
                        tooltip={poaItem ? "" : "(0 will reset to the original cost for non-POA Suburbs/Postcodes)"}
                        value={newFreightPOA?.Price}
                        min={0}
                        max={9999}
                        autoFocus
                        tight={true}
                        preValidate={"numeric"}
                        onEnter={(e) => {
                            setNewFreightPOA((prevState) => ({
                                ...prevState,
                                Price: Number(e.target.value ? e.target.value : e),
                            }));
                        }}
                        onChange={(val) => setNewFreightPOA((prevState) => ({ ...prevState, Price: Number(val) }))}
                    />
                    {user.Role >= 90 && (
                        <BSL_TextInput
                            label={`${poaItem ? "Supplier" : targetProductFreightPOA} Invoice Number`}
                            style={{ width: "240px", color: "white" }}
                            value={newFreightPOA?.Invoice}
                            tight={true}
                            maxLength={25}
                            onEnter={(e) =>
                                setNewFreightPOA((prevState) => ({ ...prevState, Invoice: e.target.value }))
                            }
                            onChange={(val) => setNewFreightPOA((prevState) => ({ ...prevState, Invoice: val }))}
                        />
                    )}
                </BSL_Dialog>
            )}
        </Paper>
    );
};
